import getLogger from '@ignitesales/logger-es';
import buildPageUrl from './page/buildPageUrl.mjs';
import extractPageFromPath from './page/extractPageFromPath.mjs';
import extractAnswersFromPageResponse from './page/extractAnswersFromPageResponse.mjs';
import extractProgressFromPageResponse from './page/extractProgressFromPageResponse.mjs';
import extractProductsFromPageResponse from './page/extractProductsFromPageResponse.mjs';
import extractValidationErrorsFromPageResponse from './page/extractValidationErrorsFromPageResponse.mjs';

const logger = getLogger({ module: 'ignite' });

let runMode = 'development';

const getRunMode = () => runMode;

const serviceWaitTimeout = 30000;

const commonServicesInjected = () => {
  return (
    ignite &&
    ignite.answerService &&
    ignite.pageResolutionService &&
    ignite.contactService &&
    ignite.noteService &&
    ignite.appointmentService
  );
};

const branchServicesInjected = (isBranchGuide) => {
  return isDetailsPage() || !isBranchGuide || ( ignite && ignite.employeeLoginService);
};

const servicesInitialized = () => {
  const waitStart = Date.now();

  return new Promise( (resolve, reject) => {
    const checkForServices = () => {
      logger.info('checking service availability');
      if (commonServicesInjected()) {
        logger.info('services ready');
        return resolve(true);
      }

      const elapsed = Date.now() - waitStart;
      if (elapsed >= serviceWaitTimeout) {
        return reject( new Error('services unavailable') );
      }

      logger.info('waiting for service availability');
      setTimeout(() => checkForServices(), 200);
    };

    checkForServices();
  });
};

const isDetailsPage = () => {
  return extractPageFromPath(window.location.pathname).type === 'details';
};

const loadInitialState = async (options = {}) => {
  await servicesInitialized();
  const currentPageRequest = extractPageFromPath(window.location.pathname);

  const initializedUser = await ignite.pageResolutionService.initializeUser();
  const { uuid, guideDelivery } = initializedUser;
  const isBranchGuide = guideDelivery.toLowerCase() === 'branch';

  if( branchServicesInjected(isBranchGuide) ) {
    logger.debug('All ignite services have been initialized for state setup');
  } else {
    logger.error('Unable to load all ignite services needed to support this guide type');
  }

  const currentPageResponse = await ignite.pageResolutionService.loadPage({
    page: currentPageRequest
  });

  const currentPageUrl = buildPageUrl({
    ...currentPageResponse.page,
    type: currentPageResponse.type,
    uuid
  });

  const answers = extractAnswersFromPageResponse(currentPageResponse);

  const localAnswers = JSON.parse(window.sessionStorage.getItem(`${uuid}-localAnswers`));

  const progress = extractProgressFromPageResponse(currentPageResponse);

  const products = extractProductsFromPageResponse(currentPageResponse);

  const validationErrors = extractValidationErrorsFromPageResponse(currentPageResponse);

  const initialState =  {
    user: { ...initializedUser },
    currentPage: currentPageUrl,
    pages: {
      [currentPageUrl]: currentPageResponse.page
    },
    answers: {
      ...localAnswers,
      ...answers
    },
    products,
    processingNavigation: false,
    runMode: ignite.getRunMode(),
    progress,
    validationErrors,
    isBranchGuide
  };

  return options.decorateStateFn ? options.decorateStateFn(initialState) : initialState;
};

const setRunMode = (newRunMode) => {
  logger.info('Setting runMode', newRunMode);
  runMode = newRunMode;
};

const ignite = {
  getRunMode,
  servicesInitialized,
  loadInitialState,
  setRunMode
};

window.ignite = ignite;

export {
  ignite as default,
  ignite,
  servicesInitialized
};
